import React from 'react';
import { CheckIcon, LockIcon, MinusIcon, TimerIcon, UserCheckIcon } from 'lucide-react';
import { AgentMark } from '../../ui/AgentMark';
import { agents } from '../../../data/agents';
import { capabilities } from '../../../data/beheer/permissions';
import type { CapabilityId, Permission, PermissionMatrix as Matrix } from '../../../types/settings';
import type { AgentId } from '../../../types/anna';
const CYCLE: Permission[] = ['zelf', 'tenzijBezwaar', 'naOk', 'nooit', 'nvt'];
export const PERMISSION_META: Record<Permission, {
  short: string;
  icon: typeof CheckIcon;
  className: string;
}> = {
  zelf: {
    short: 'Zelf',
    icon: CheckIcon,
    className: 'bg-white/[0.06] text-ink-soft'
  },
  tenzijBezwaar: {
    short: 'Tenzij bezwaar',
    icon: TimerIcon,
    className: 'bg-accent/10 text-accent'
  },
  naOk: {
    short: 'Na ok',
    icon: UserCheckIcon,
    className: 'bg-ink text-night-950'
  },
  nooit: {
    short: 'Nooit',
    icon: LockIcon,
    className: 'border border-dashed border-white/20 text-ink-muted'
  },
  nvt: {
    short: '—',
    icon: MinusIcon,
    className: 'text-ink-muted/60'
  }
};
interface PermissionMatrixProps {
  matrix: Matrix;
  onChange: (agent: AgentId, cap: CapabilityId, next: Permission) => void;
}

/** Who may do what, and at which level. Tap a cell to step through the levels; fixed rules are locked. */
export function PermissionMatrix({
  matrix,
  onChange
}: PermissionMatrixProps) {
  return <div className="no-scrollbar overflow-x-auto rounded-3xl border border-white/[0.07] bg-night-900" data-id="element-50" data-mp="2amkz7vvi0w5t">
      <table className="w-full min-w-[1080px] text-left text-[13px]" data-id="element-51" data-mp="25o9nffln8bky">
        <thead data-id="element-52" data-mp="25q460n9fzrlv">
          <tr className="border-b border-white/[0.06]" data-id="element-53" data-mp="2jn98yedv0acw">
            <th scope="col" className="sticky left-0 z-10 bg-night-900 px-5 py-3 text-[12px] font-medium text-ink-muted" data-id="element-54" data-mp="2jmq33w1hhcf8">
              Agent
            </th>
            {capabilities.map(c => <th key={c.id} scope="col" className="px-2 py-3 align-bottom" title={c.hint} data-id="element-55" data-mp="p9rx3omth1xp">
                <span className={`flex items-center gap-1 whitespace-nowrap text-[12px] font-medium ${c.locked ? 'text-ink-muted' : 'text-ink-soft'}`} data-id="element-56" data-mp="l9hftftkqp3j">
                  {c.locked && <LockIcon className="h-3 w-3" aria-hidden data-id="element-57" data-mp="2pay62biptln0" />}
                  {c.label}
                </span>
              </th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.05]" data-id="element-58" data-mp="2z9xcdng0d56u">
          {agents.map(a => <tr key={a.id} data-id="element-59" data-mp="33bioh6kpxffz">
              <th scope="row" className="sticky left-0 z-10 bg-night-900 px-5 py-2.5 font-normal" data-id="element-60" data-mp="33cakgii89f6s">
                <span className="flex items-center gap-2.5 whitespace-nowrap text-[14px] text-ink" data-id="element-61" data-mp="389typiexdryq">
                  <AgentMark agentId={a.id} size="sm" data-id="element-62" data-mp="3d129ahrzrjdh" /> {a.name}
                </span>
              </th>
              {capabilities.map(c => {
            const value = matrix[a.id][c.id];
            const meta = PERMISSION_META[value];
            const Icon = meta.icon;
            const next = CYCLE[(CYCLE.indexOf(value) + 1) % CYCLE.length];
            return <td key={c.id} className="px-2 py-2.5" data-id="element-63" data-mp="1fe019d5ljaec">
                    <button type="button" disabled={c.locked} onClick={() => onChange(a.id, c.id, next)} aria-label={`${a.name}, ${c.label}: ${meta.short}${c.locked ? ', vaste regel' : `. Wijzig naar ${PERMISSION_META[next].short}`}`} className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[12px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:cursor-not-allowed ${meta.className} ${c.locked ? '' : 'hover:ring-1 hover:ring-white/25'}`} data-id="element-64" data-mp="1fbexgilqmkdy">
                      <Icon className="h-3 w-3" strokeWidth={2} aria-hidden data-id="element-65" data-mp="2hsi4lmkwmiho" />
                      {meta.short}
                    </button>
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>
    </div>;
}